/*
 * visitor-ip-country.js
 *
 * Populates the "Hidden" field of the Globo Form Builder quote form on the
 * Ask For Quote page with the visitor's IP address and country.
 *
 * Markup Globo renders:
 *   <div data-type="hidden" data-element-id="hidden-1" style="display:none">
 *     <input type="hidden" data-type="fixed" id="hidden-1" name="hidden-1"
 *            value="{{hidden-1}}">
 *   </div>
 * Note data-element-id sits on the WRAPPER — the value lives on the input
 * inside it.
 *
 * Globo is a Vue app that mounts after this script runs and keeps its own copy
 * of the field value, so we: wait for the input to appear, set it through the
 * native value setter, fire input/change so Vue's model updates, and re-apply
 * if Globo re-renders or restores its "{{hidden-1}}" placeholder.
 */
(function () {
  'use strict';

  var FIELD_SELECTORS = [
    '[data-element-id="hidden-1"] input',
    'input#hidden-1',
    'input[name="hidden-1"]'
  ];
  var CACHE_KEY = 'nt_visitor_ip_country';
  var LOOKUP_TIMEOUT = 5000;

  // Free, keyless, HTTPS endpoints. Tried in order until one answers.
  var PROVIDERS = [
    {
      url: 'https://ipwho.is/?fields=ip,country,country_code,success',
      parse: function (data) {
        if (data && data.success === false) return null;
        return normalize(data && data.ip, data && data.country, data && data.country_code);
      }
    },
    {
      url: 'https://ipapi.co/json/',
      parse: function (data) {
        return normalize(data && data.ip, data && data.country_name, data && data.country_code);
      }
    },
    {
      url: 'https://api.country.is/',
      parse: function (data) {
        return normalize(data && data.ip, null, data && data.country);
      }
    }
  ];

  function normalize(ip, country, code) {
    if (!ip) return null;
    return {
      ip: String(ip),
      country: country ? String(country) : '',
      countryCode: code ? String(code).toUpperCase() : ''
    };
  }

  // "203.0.113.5 | United States (US)"
  function format(info) {
    var parts = [info.ip];
    var place = info.country;
    if (info.countryCode) {
      place = place ? place + ' (' + info.countryCode + ')' : info.countryCode;
    }
    if (place) parts.push(place);
    return parts.join(' | ');
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var info = JSON.parse(raw);
      return info && info.ip ? info : null;
    } catch (err) {
      return null;
    }
  }

  function writeCache(info) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(info));
    } catch (err) {
      /* private mode / storage full — cache is optional */
    }
  }

  function fetchJson(url) {
    if (!window.fetch) return Promise.reject(new Error('fetch unsupported'));

    var controller = window.AbortController ? new AbortController() : null;
    var timer = setTimeout(function () {
      if (controller) controller.abort();
    }, LOOKUP_TIMEOUT);

    return fetch(url, {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store',
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        clearTimeout(timer);
        return data;
      })
      .catch(function (err) {
        clearTimeout(timer);
        throw err;
      });
  }

  function lookup(index) {
    index = index || 0;
    if (index >= PROVIDERS.length) return Promise.resolve(null);

    var provider = PROVIDERS[index];
    return fetchJson(provider.url)
      .then(function (data) {
        return provider.parse(data) || lookup(index + 1);
      })
      .catch(function () {
        return lookup(index + 1);
      });
  }

  var visitorValue = null;

  function findFields() {
    var seen = [];
    for (var i = 0; i < FIELD_SELECTORS.length; i++) {
      var found = document.querySelectorAll(FIELD_SELECTORS[i]);
      for (var j = 0; j < found.length; j++) {
        if (seen.indexOf(found[j]) === -1) seen.push(found[j]);
      }
    }
    return seen;
  }

  // Vue/React track the value via a property descriptor on the prototype, so
  // assigning field.value directly can be ignored. Go through the native setter.
  var nativeSetter = (function () {
    try {
      var desc = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
      return desc && desc.set ? desc.set : null;
    } catch (err) {
      return null;
    }
  })();

  function fill(field) {
    if (!field || !visitorValue) return;
    if (field.value === visitorValue) return;

    if (nativeSetter) {
      nativeSetter.call(field, visitorValue);
    } else {
      field.value = visitorValue;
    }

    // Let Globo's Vue model (and any other listener) know the field changed.
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
    field.dispatchEvent(new Event('blur', { bubbles: true }));
  }

  function fillAll() {
    var fields = findFields();
    for (var i = 0; i < fields.length; i++) fill(fields[i]);
    return fields.length > 0;
  }

  function watchForField() {
    if (!window.MutationObserver) return;

    var observer = new MutationObserver(function () {
      fillAll();
    });

    // Globo rewrites the value attribute on re-render, so watch attributes too.
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value']
    });

    // Stop watching after 60s — the form is either there or it isn't.
    setTimeout(function () {
      observer.disconnect();
    }, 60000);
  }

  // Globo mounts asynchronously and can restore its "{{hidden-1}}" placeholder,
  // so poll for a while on top of the observer.
  function pollForField() {
    var elapsed = 0;
    var interval = 500;
    var timer = setInterval(function () {
      elapsed += interval;
      fillAll();
      if (elapsed >= 60000) clearInterval(timer);
    }, interval);
  }

  // Last chance: make sure the value is present at submit time. Globo submits
  // via its own button click rather than a native form submit, so cover both.
  document.addEventListener('submit', fillAll, true);
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      if (target.closest('button, input[type="submit"], .globo-form-app, [class*="gfb"]')) {
        fillAll();
      }
    },
    true
  );

  function start() {
    var cached = readCache();
    if (cached) {
      visitorValue = format(cached);
      fillAll();
    }

    watchForField();
    pollForField();

    if (cached) return;

    lookup(0).then(function (info) {
      if (!info) return;
      writeCache(info);
      visitorValue = format(info);
      fillAll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
